// useLocationBeacon — 2026-05-30. While a learner is signed in, low-frequency
// (throttled ~45s) write their GPS to /users/{userId}.lastLocation so the
// worker admin map's「顯示學員」toggle can show where learners are.
//
// Design constraints (per plan):
//   - Fire-and-forget: errors are swallowed; a failed location write must never
//     break the app. Mirrors usePushSubscribe's fcmToken write.
//   - Throttled to one write per `intervalMs` (default 45s) via useThrottled.
//   - Skips workers / preview accounts so test sessions don't pollute the data.
//   - Uses the REAL coords only (callers pass realGeo.coords, not simulated).
//
// 2026-05-30 anti-drift (what pro nav apps do, within PWA limits — no road
// matching / sensor fusion available):
//   1. ACCURACY FILTER: ignore fixes whose reported accuracy is missing or
//      worse than MAX_ACCURACY_M. A bad fix is the usual cause of「跳遠」, so
//      we simply don't feed it to the beacon (last good position stays).
//   2. SMOOTHING: keep the last few ACCEPTED fixes and report their moving
//      average, so the dot glides instead of snapping. Recent-weighted so a
//      real move still tracks. Trade-off: a fast move lags slightly.

import { userDoc } from "@goodbye/firebase";
import { serverTimestamp, updateDoc } from "firebase/firestore";
import { useEffect, useRef } from "react";
import { useThrottled } from "./useThrottled";

/** Drop fixes worse than this (meters). City/forest GPS routinely reports
 *  20-40m; beyond this is almost always a bad bounce that would jump the dot.
 *  2026-05-30: briefly tried 100m to rescue two non-reporting learners
 *  (蔡佩軒/張凱吏) but it made no difference — their phones weren't reporting at
 *  all (not a threshold issue), so reverted to 50m to keep everyone's dot
 *  tighter. */
const MAX_ACCURACY_M = 50;
/** How many recent good fixes to average over. 3 ≈ smooth without much lag. */
const SMOOTH_WINDOW = 3;

export interface LocationBeaconInput {
  /** Learner doc id (u01…). Null when not signed in → no write. */
  userId: string | null;
  /** Real GPS coords (NOT simulated). Null until a fix arrives. */
  coords: { lat: number; lng: number } | null;
  /** GPS accuracy radius in meters (from navigator.geolocation). Null = unknown
   *  → treated as a bad fix and dropped. */
  accuracy?: number | null;
  /** Workers / preview QA sessions don't report location. */
  isWorker: boolean;
  /** Throttle window in ms. Default 45000 (≈1.3 writes/min/learner). */
  intervalMs?: number;
}

export function useLocationBeacon({
  userId,
  coords,
  accuracy,
  isWorker,
  intervalMs = 45000,
}: LocationBeaconInput): void {
  // Ring buffer of recent ACCEPTED fixes (passed the accuracy filter), used to
  // compute the smoothed position. Lives across renders.
  const recentRef = useRef<{ lat: number; lng: number }[]>([]);

  // Trailing-edge throttle: at most one write per window, latest value wins.
  const write = useThrottled((lat: number, lng: number) => {
    if (!userId) return;
    void updateDoc(userDoc(userId), {
      lastLocation: { lat, lng, at: serverTimestamp() },
    }).catch(() => {
      // Swallow — fire-and-forget. A failed beacon must not affect the app.
    });
  }, intervalMs);

  useEffect(() => {
    if (!userId || isWorker || !coords) return;
    // 1. ACCURACY FILTER — drop missing / poor fixes; keep last good position.
    if (typeof accuracy !== "number" || accuracy > MAX_ACCURACY_M) return;

    // 2. SMOOTHING — push this good fix, keep the last SMOOTH_WINDOW, report
    //    their average so the dot glides rather than snaps.
    const buf = recentRef.current;
    buf.push({ lat: coords.lat, lng: coords.lng });
    if (buf.length > SMOOTH_WINDOW) buf.shift();
    const avg = buf.reduce(
      (acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }),
      { lat: 0, lng: 0 },
    );
    write(avg.lat / buf.length, avg.lng / buf.length);
  }, [userId, isWorker, coords, accuracy, write]);
}
